import { useEffect, useMemo } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { LocationModel } from 'src/models/locations/location.model';
import { LocationInnerFormData } from '../LocationMultiControl/location-form.models';
import { LocationFormFields } from '../LocationMultiControl/LocationMultiControlParts/LocationFormFields';
import { usePeliusAdaptors } from '../LocationMultiControl/pelias-adaptor.hook';

interface Props {
    onChange: (location: LocationModel) => void;
    defaultValue: LocationModel | undefined;
    defaultCountryCode: string;
    name: string;
}

export const LocationSingleControl = ({
    name,
    defaultValue,
    onChange,
    defaultCountryCode,
}: Props): React.ReactElement => {
    const {
        formState: { isValidating: isParentFormValidating },
    } = useFormContext();
    const innerId = `${name}-location-inner`;

    const { peliasToLocationAdaptor, singleLocationToPeliasAdaptor } = usePeliusAdaptors();

    const innerDefaultValue = useMemo(() => {
        return singleLocationToPeliasAdaptor(innerId, defaultCountryCode, defaultValue);
    }, [defaultCountryCode, defaultValue, innerId, singleLocationToPeliasAdaptor]);

    const formMethods = useForm<LocationInnerFormData>({
        mode: 'all',
        defaultValues: {
            [innerId]: innerDefaultValue,
        },
    });

    const value = formMethods.watch();

    useEffect(() => {
        const pelias = value[innerId];
        const changeValue = peliasToLocationAdaptor(pelias);
        onChange(changeValue);
    }, [innerId, onChange, peliasToLocationAdaptor, value]);

    useEffect(() => {
        if (isParentFormValidating) {
            void formMethods.trigger();
        }
    }, [formMethods, isParentFormValidating]);

    return (
        <FormProvider {...formMethods}>
            <LocationFormFields id={innerId} isSingle />
        </FormProvider>
    );
};
